import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import EditIcon from '@material-ui/icons/EditOutlined';
import { useObserver } from 'mobx-react-lite';
import moment from 'moment-timezone';
import React from 'react';
import { Link, LinkProps } from 'react-router-dom';
import EnhancedTableHead, { getSorting, HeadRow, Order, sortRows } from '../../common/EnhancedTableHead';
import LinkRouter from '../../common/LinkRouter';
import TimestampTooltip from '../../common/TimestampTooltip';

interface Props {
  paramSets: any[];
  accountId: string;
  region: string;
  page: number;
  onPageChange: (newPage: number) => void;
}

const headRows: HeadRow[] = [
  { id: 'name', disablePadding: true, label: 'Name' },
  { id: 'version', disablePadding: false, label: 'Latest Version' },
  { id: 'lastModified', disablePadding: false, label: 'Last Modified' },
  { id: 'actions', disablePadding: false, label: '' }
];

const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <Link innerRef={ref as any} {...props} />
));

function FaroParamSetsTable(props: Props) {
  const { paramSets, accountId, region, page, onPageChange } = props;
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<string>('version');
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  function handleRequestSort(event: React.MouseEvent<unknown>, property: string) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    onPageChange(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const sortedRows = sortRows(paramSets, getSorting(order, orderBy));

  return useObserver(() => (
    <div>
      <Table>
        <EnhancedTableHead
          order={order}
          orderBy={orderBy}
          fields={headRows}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
            <TableRow key={row.name}>
              <TableCell component="th" scope="row">
                <LinkRouter to={`/faro/parameters/${accountId}/${region}/${row.name}?version=${row.version}`}>{row.name}</LinkRouter>
              </TableCell>
              <TableCell>{row.version}</TableCell>
              <TableCell>
                <TimestampTooltip timestamp={row.version}>
                  <div>{moment(row.version).fromNow()}</div>
                </TimestampTooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Edit Parameter Set">
                  <IconButton
                    size="small"
                    to={`/faro/parameters/${accountId}/${region}/${row.name}/edit`}
                    component={AdapterLink}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={paramSets.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          'aria-label': 'previous page',
        }}
        nextIconButtonProps={{
          'aria-label': 'next page',
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </div>
  ));
};

export default FaroParamSetsTable;
